import React, {useEffect, useRef} from 'react'
import {Button, HStack, Input, useNumberInput, useStyleConfig} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import MinusIcon from '../../components/icons/action/MinusIcon'
import AddIcon from '../../components/icons/action/AddIcon'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {getPriceData} from '../../utils/product-utils'

const QuantityPicker = ({
    productName,
    hideIncDecButtons = false,
    productData,
    variant,
    isProductItem = false,
    isReturnOrder = false,
    ...props
}) => {
    const intl = useIntl()
    const style = useStyleConfig('QuantityPicker', {variant})
    const {addLayer} = useGoogleTagManager()
    const {currency} = useCurrency()
    const previousValue = useRef(props?.value)

    useEffect(() => {
        if (isProductItem && previousValue.current !== props?.value) {
            const isIncremented = props?.value > previousValue.current
            const previous = previousValue.current
            const current = props?.value
            previousValue.current = props?.value
            const event = isIncremented ? 'add_to_cart' : 'remove_from_cart'
            const triggerGTMEvent = async () => {
                await addLayer(polish_ecommerce_gtm_event)
                await addLayer(
                    item_gtm_event.bind(this, {
                        event,
                        currency: currency,
                        bundle: 'false', //? Is this correct?
                        items: [
                            {
                                item_name: productData?.c_defaultName,
                                item_id: productData?.id,
                                item_category: productData?.c_defaultCategoryName,
                                item_variant: productData?.c_subModelIdentifier,
                                price: parseFloat(
                                    (
                                        (productData?.price - productData?.tax) /
                                        productData?.quantity
                                    ).toFixed(2),
                                ),
                                fullprice: getPriceData(productData)?.isOnSale
                                    ? 'reduced'
                                    : 'fullprice',
                                rating_points: '', //?Value undecided or missing
                                erp_category: productData?.c_MaterialGroup,
                                prdha_category: productData?.c_prdhaCategory,
                                quantity: isIncremented ? current - previous : previous - current,
                            },
                        ],
                    }),
                )
            }

            triggerGTMEvent()
        }
    }, [props?.value, addLayer, currency, productData, isProductItem])

    const {getInputProps, getIncrementButtonProps, getDecrementButtonProps} = useNumberInput({
        ...props,
        focusInputOnChange: false,
        onFocus: (e) => {
            // eslint-disable-next-line react/prop-types
            const {onFocus} = props
            e.target.select()
            // eslint-disable-next-line react/prop-types
            onFocus?.call(this, e)
        },
    })

    const inc = getIncrementButtonProps({
        'aria-label': intl.formatMessage(
            {
                defaultMessage: 'Increment Quantity for {productName}',
                id: 'product_view.label.assistive_msg.quantity_increment',
            },
            {productName},
        ),
    })

    const dec = getDecrementButtonProps({
        'aria-label': intl.formatMessage(
            {
                defaultMessage: 'Decrement Quantity for {productName}',
                id: 'product_view.label.assistive_msg.quantity_decrement',
            },
            {productName},
        ),
    })

    const input = getInputProps({
        'aria-label': intl.formatMessage({
            defaultMessage: 'Quantity',
            id: 'product_view.label.quantity',
        }),
    })

    // Accessibility improvements:
    inc.tabIndex = 0
    dec.tabIndex = 0

    inc.onKeyDown = (evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
            evt.preventDefault()
            evt.key = 'ArrowUp'
            input.onKeyDown(evt)
        }
    }

    dec.onKeyDown = (evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
            evt.preventDefault()
            evt.key = 'ArrowDown'
            input.onKeyDown(evt)
        }
    }

    return (
        <HStack {...style?.stack}>
            {!hideIncDecButtons && (
                <Button {...dec} tabIndex={0} data-testid="dec-button" display={'flex'}>
                    <MinusIcon
                        {...(props?.value > 1 ? {...style.decButtonActive} : {...style?.decButton})}
                        data-testid="quantity-decrement"
                    />
                </Button>
            )}
            <Input
                {...(isReturnOrder ? style?.inputReturn : style?.input)} // Apply conditional style
                {...input}
                data-testid="spinbutton"
            />
            {!hideIncDecButtons && (
                <Button {...inc} tabIndex={0} data-testid="inc-button" display={'flex'}>
                    <AddIcon
                        {...(props.value >= props?.max
                            ? {...style?.incButton}
                            : {...style.incButtonActive})}
                        data-testid="quantity-increment"
                    />
                </Button>
            )}
        </HStack>
    )
}

QuantityPicker.propTypes = {
    productName: PropTypes.string,
    hideIncDecButtons: PropTypes.bool,
    variant: PropTypes.string,
    value: PropTypes.any,
    max: PropTypes.number,
    productData: PropTypes.object,
    isProductItem: PropTypes.bool,
    isReturnOrder: PropTypes.bool,
}

export default QuantityPicker
